import { useDisclosure } from "@mantine/hooks"
import { Modal, Button, Flex, Loader, Text, ScrollArea, Group, Badge } from "@mantine/core"
import { observer } from "mobx-react-lite"
import { ScheduleDraftSearch } from "./ScheduleDraftSearch"
import InfiniteScroll from "react-infinite-scroll-component"
import { DraftScheduleEventStore } from "../../app/store"
import { type Dispatch, type SetStateAction, useEffect } from "react"
import { ScheduleDraftElementCard } from "../../entities/schedule"
import { type ScheduleEvent } from "../../shared/types/scheduleEvent"
import { ErrorResult } from "../../shared/ui/ErrorResult"
import { EmptyResult } from "../../shared/ui/EmptyResult"

// ----------------------------------------------------------------------

type Props = {
  setDraft: Dispatch<SetStateAction<ScheduleEvent | null>>
}

export const ScheduleFillFromDraftModal = observer(function ScheduleFillFromDraftModal({ setDraft }: Props) {
  const [ opened, { open, close } ] = useDisclosure(false)
  const { drafts, loadMoreDrafts, isLoading, error, totalCount, getDrafts } = DraftScheduleEventStore

  useEffect(() => {
    if (opened) {
      void getDrafts()
    }
  }, [ getDrafts, opened ])

  const renderInfo = () => {
    if (isLoading) {
      return (
        <Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
          <Loader size="lg" variant="dots"/>
          <Text size="lg" mt="xs">
            Загружаем данные...
          </Text>
        </Flex>
      )
    }

    if (error) {
      return <ErrorResult errorMessage={error}/>
    }

    if (!drafts.length) {
      return <EmptyResult/>
    }

    return (
      <Group position="center">
        <Badge color="gray" size="md">
          Вы загрузили все шаблоны
        </Badge>
      </Group>
    )
  }

  return (
    <>
      <Modal.Root
        zIndex={1001}
        size="md"
        centered
        opened={opened}
        onClose={close}
      >
        <Modal.Overlay/>
        <Modal.Content
          sx={{ overflow: "hidden" }}
          radius="md"
        >
          <Modal.Header>
            <Modal.Title>Выбор шаблона расписания</Modal.Title>
            <Modal.CloseButton/>
          </Modal.Header>
          <Modal.Body>
            <ScheduleDraftSearch/>

            <ScrollArea.Autosize
              viewportProps={{ id: "drafts-modal", style: { maxHeight: "calc(90dvh - 134px)" } }}
            >
              <InfiniteScroll
                scrollableTarget="drafts-modal"
                next={loadMoreDrafts}
                dataLength={drafts.length}
                hasMore={totalCount !== drafts.length}
                loader={<Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
                  <Loader size="lg" variant="dots"/>
                  <Text size="lg" mt="xs">
                    Загружаем данные...
                  </Text>
                </Flex>}
                endMessage={renderInfo()}
                style={{ overflow: "unset" }}
              >
                {drafts.map((draft) => (
                  <ScheduleDraftElementCard close={close} setDraft={setDraft} key={draft._id} draft={draft}/>
                ))}
              </InfiniteScroll>

            </ScrollArea.Autosize>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>

      <Button compact variant="light" onClick={open}>
        Заполнить из шаблона
      </Button>
    </>
  )
})
